<template>
  <div>
    <div class="row">
      <div class="col-12 mt-3">
        <Card body-classes="p-3" card-classes="shadow-sm">
          <template #header>
            <div class="d-flex flex-row align-items-center justify-content-between">
              <h5 class="mb-0 text-primary">
                <vue-feather type="activity" class="me-2"></vue-feather>
                Sales Trends - {{ store.name }}
              </h5>
              <div class="d-flex align-items-center gap-3">
                <div class="d-flex align-items-center">
                  <label for="period" class="me-2 text-muted">Period:</label>
                  <select id="period" class="form-select form-select-sm" v-model="period">
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div class="d-flex align-items-center">
                  <label for="fromDate" class="me-2 text-muted">From:</label>
                  <input type="date" id="fromDate" class="form-control form-control-sm" v-model="fromDate">
                </div>
                <div class="d-flex align-items-center">
                  <label for="toDate" class="me-2 text-muted">To:</label>
                  <input type="date" id="toDate" class="form-control form-control-sm" v-model="toDate">
                </div>
                <button class="btn btn-primary btn-sm" @click="getReport">
                  Generate Report
                </button>
              </div>
            </div>
          </template>
          <div>
            <div v-if="loading" class="text-center py-5">
              <Spinner spinner-class="mb-1" />
              <p class="text-muted mt-2">Loading sales trends...</p>
            </div>
            <div v-else-if="reportData">
              <div class="row mb-4">
                <div class="col-md-3">
                  <div class="card shadow-sm">
                    <div class="card-body text-center">
                      <vue-feather type="shopping-cart" size="24" class="text-primary mb-2"></vue-feather>
                      <h6 class="text-muted mb-1">Total Sales</h6>
                      <h4 class="mb-0 text-primary">{{ reportData.summary?.total_sales || 0 }}</h4>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card shadow-sm">
                    <div class="card-body text-center">
                      <vue-feather type="dollar-sign" size="24" class="text-success mb-2"></vue-feather>
                      <h6 class="text-muted mb-1">Total Revenue</h6>
                      <h4 class="mb-0 text-success">{{ formatNumber(reportData.summary?.total_revenue || 0) }}</h4>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card shadow-sm">
                    <div class="card-body text-center">
                      <vue-feather type="percent" size="24" class="text-warning mb-2"></vue-feather>
                      <h6 class="text-muted mb-1">Total Tax</h6>
                      <h4 class="mb-0 text-warning">{{ formatNumber(reportData.summary?.total_tax || 0) }}</h4>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card shadow-sm">
                    <div class="card-body text-center">
                      <vue-feather type="trending-up" size="24" class="text-info mb-2"></vue-feather>
                      <h6 class="text-muted mb-1">Avg Sale Value</h6>
                      <h4 class="mb-0 text-info">{{ formatNumber(reportData.summary?.average_sale_value || 0) }}</h4>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card shadow-sm mb-3">
                <div class="card-body">
                  <Line :data="chartData" :options="chartOptions" />
                </div>
              </div>
            </div>
            <div v-else class="alert alert-info">
              <vue-feather type="info" size="18" class="me-2"></vue-feather>
              No data available for the selected period.
            </div>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>
import {decrypt} from "@/utils/crypto";
import analyticsService from "@/api/analyticsService";
import formatNumber from "@/utils/formatNumber";
import { Line } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export default {
  name: "SalesTrends",
  components: { Line },

  data(){
    return {
      profile: {},
      store: {},
      loading: false,
      period: 'daily',
      fromDate: '',
      toDate: '',
      reportData: null
    }
  },

  methods: {
    formatNumber,
    async getReport() {
      if (!this.fromDate || !this.toDate) {
        return;
      }

      this.loading = true;
      try {
        const params = {
          store_id: this.store.id,
          period: this.period,
          from: this.fromDate,
          to: this.toDate
        };

        const response = await analyticsService.getSalesTrends(params);
        this.reportData = response || null;
      } catch(e) {
        console.error(e);
        this.reportData = null;
      } finally {
        this.loading = false;
      }
    },
    setDefaultDates() {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth();
      
      const firstDay = new Date(year, month, 1);
      const fromYear = firstDay.getFullYear();
      const fromMonth = String(firstDay.getMonth() + 1).padStart(2, '0');
      const fromDay = String(firstDay.getDate()).padStart(2, '0');
      
      const toYear = today.getFullYear();
      const toMonth = String(today.getMonth() + 1).padStart(2, '0');
      const toDay = String(today.getDate()).padStart(2, '0');

      this.fromDate = `${fromYear}-${fromMonth}-${fromDay}`;
      this.toDate = `${toYear}-${toMonth}-${toDay}`;
    }
  },

  computed: {
    chartData() {
      if (!this.reportData || !this.reportData.chart) {
        return { labels: [], datasets: [] };
      }

      return {
        labels: this.reportData.chart.labels || [],
        datasets: [
          {
            label: 'Revenue',
            data: this.reportData.chart.datasets.find(d => d.label === 'Revenue')?.data || [],
            borderColor: '#28a745',
            backgroundColor: 'rgba(40, 167, 69, 0.1)',
            tension: 0.4
          },
          {
            label: 'Sales Count',
            data: this.reportData.chart.datasets.find(d => d.label === 'Sales Count')?.data || [],
            borderColor: '#007bff',
            backgroundColor: 'rgba(0, 123, 255, 0.1)',
            tension: 0.4
          },
          {
            label: 'Tax',
            data: this.reportData.chart.datasets.find(d => d.label === 'Tax')?.data || [],
            borderColor: '#ffc107',
            backgroundColor: 'rgba(255, 193, 7, 0.1)',
            tension: 0.4
          },
          {
            label: 'Discount',
            data: this.reportData.chart.datasets.find(d => d.label === 'Discount')?.data || [],
            borderColor: '#dc3545',
            backgroundColor: 'rgba(220, 53, 69, 0.1)',
            tension: 0.4
          }
        ]
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Sales Trends Over Time'
          }
        },
        scales: {
          y: {
            beginAtZero: true
          }
        }
      };
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')));
    this.setDefaultDates();
    await this.getReport();
  }
}
</script>

<style scoped>
.table th {
  background-color: #f8f9fa;
  font-weight: 600;
}
canvas {
  height: 400px !important;
}
</style>
